import Button from '@/components/base/Button';
import { useTypeOnce } from '@/components/motion/motionHelpers';

interface ReportInsight {
  title: string;
  body: string;
  bullets: string[];
}

interface ReportExportRow {
  id: string;
  name: string;
  format: string;
  period: string;
  date: string;
  by: string;
}

interface ReportInsightsPanelProps {
  insight: ReportInsight;
  canExport: boolean;
  onExport: () => void;
  exports: ReportExportRow[];
}

export default function ReportInsightsPanel({
  insight,
  canExport,
  onExport,
  exports,
}: ReportInsightsPanelProps) {
  // The headline types itself, then the explanation and points follow.
  const headline = useTypeOnce(insight.title);
  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
          <span className="ai-spark flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
            <i className="ri-sparkling-2-line" />
          </span>
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950">AI summary</h3>
            <p className="mt-0.5 text-xs text-foreground-500">What the numbers are saying</p>
          </div>
        </header>
        <div className="px-5 py-4">
          <h4 className="min-h-[1.25rem] font-heading text-sm font-semibold text-foreground-900" aria-label={insight.title}>
            <span aria-hidden="true" className={headline.done ? '' : 'ai-caret'}>
              {headline.shown}
            </span>
          </h4>
          {headline.done ? (
            <>
              <p className="mt-1.5 animate-fade-rise text-sm text-foreground-600">{insight.body}</p>
              <ul className="ai-points mt-3 space-y-2">
                {insight.bullets.map((bullet, index) => (
                  <li key={bullet} className="flex items-start gap-2 text-xs text-foreground-700" style={{ animationDelay: `${150 + index * 110}ms` }}>
                    <i className="ri-arrow-right-s-line mt-0.5 text-accent-600" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </section>

      <section className="rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950">Export & share</h3>
            <p className="mt-0.5 text-xs text-foreground-500">CSV or PDF, ready for the client</p>
          </div>
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-base text-secondary-800">
            <i className="ri-download-2-line" />
          </span>
        </header>
        <div className="px-5 py-4">
          <Button
            icon="ri-download-2-line"
            className="join-shine w-full"
            disabled={!canExport}
            onClick={onExport}
          >
            Export report
          </Button>
          <p className="mt-2 text-xs text-foreground-500">
            {canExport
              ? 'Choose CSV for spreadsheets or PDF for a client-ready document.'
              : 'Your role can view reports but not export them.'}
          </p>
        </div>
        <div className="border-t border-background-100 px-5 py-4">
          <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            Recent exports
          </p>
          {exports.length === 0 ? (
            <p className="mt-2.5 text-xs text-foreground-500">No exports recorded yet.</p>
          ) : null}
          <ul className="mt-2.5 space-y-2.5">
            {exports.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-foreground-800">{item.name}</p>
                  <p className="text-[11px] text-foreground-400">
                    {item.period} · {item.by}
                  </p>
                </div>
                <span className="shrink-0 rounded-full border border-background-300 px-2 py-0.5 text-[10px] font-label font-medium text-foreground-600">
                  {item.format}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}